import { IconButton, LuIcon, Spinner, toast } from "@overmap-ai/blocks"
import { ERROR_MSG_UNEXPECTED, isAbortError, type Report } from "@overmap-ai/common"
import { useAbortableCallback } from "@overmap-ai/hooks"
import { saveAs } from "file-saver"
import { memo, useState } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"

export const ReportDownloadButton = memo(({ report }: { report: Report }) => {
	const [isDownloading, setIsDownloading] = useState(false)

	const handleDownload = useAbortableCallback(
		async (abortController) => {
			if (!report.file_sha1 || !report.file_name || !report.file_extension) {
				toast.error({
					title: "Unable to download report",
					description: "The report is missing file metadata. Refresh the reports list and try again.",
				})
				return
			}

			setIsDownloading(true)

			try {
				const url = await sdk.reports.getPresignedUrl(report.uuid, abortController.signal)

				if (abortController.signal.aborted) return

				if (url === null) {
					toast.info({ title: "No file is available for this report" })
					return
				}

				const file = await sdk.files.fetchFileFromUrl(
					url,
					report.file_sha1,
					report.file_name,
					abortController.signal,
				)

				if (abortController.signal.aborted) return

				saveAs(file, file.name)
			} catch (e: unknown) {
				if (isAbortError(e) || abortController.signal.aborted) return
				toast.error({
					title: "Failed to download Report",
					description: e instanceof Error ? e.message : ERROR_MSG_UNEXPECTED,
				})
			} finally {
				if (!abortController.signal.aborted) {
					setIsDownloading(false)
				}
			}
		},
		[report],
	)

	const disabled = isDownloading || !report.file_sha1 || !report.file_name || !report.file_extension

	return (
		<IconButton variant="soft" size="sm" accentColor="base" disabled={disabled} onClick={handleDownload}>
			{isDownloading ? <Spinner accentColor="base" size="sm" /> : <LuIcon icon={modelIconMapping.download} />}
		</IconButton>
	)
})
ReportDownloadButton.displayName = "ReportDownloadButton"
